import { Component, DestroyRef, inject } from '@angular/core';
import {
  MAT_DIALOG_DATA,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle
} from '@angular/material/dialog';
import { FormArray, FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { BasicInformationComponent } from './components/basic-information/basic-information.component';
import { createUserDetailsForm, Party, PartyFormGroup } from './models/user-details-form';
import { SelectionListComponent } from './components/selection-list/selection-list.component';
import { catchError, distinctUntilChanged, EMPTY, map, NEVER, switchMap, tap } from 'rxjs';
import { UserEntitlementOption, UserEntitlements } from './models/entitlements';
import { EntitlementsService } from '../../services/entitlements.service';
import {
  MatAccordion,
  MatExpansionPanel,
  MatExpansionPanelDescription,
  MatExpansionPanelHeader,
  MatExpansionPanelTitle
} from '@angular/material/expansion';
import { MatIcon } from '@angular/material/icon';
import { UsersService } from '../../services/users.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PartiesTableComponent } from './components/parties-table/parties-table.component';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { ModuleName } from '../../../../../../models/user';
import { UserService } from '../../../../../../services/user.service';
import { startWith } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandlerService } from '../../../../../../utils/cross-xss-script-error-handler-service';

@Component({
  selector: 'app-user-details',
  imports: [
    ReactiveFormsModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    BasicInformationComponent,
    SelectionListComponent,
    MatAccordion,
    MatExpansionPanel,
    MatExpansionPanelTitle,
    MatExpansionPanelHeader,
    MatExpansionPanelDescription,
    MatIcon,
    PartiesTableComponent,
    MatProgressSpinner
  ],
  standalone: true,
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.scss'
})
export class UserDetailsComponent {
  data = inject<{ id?: string; copyMode?: boolean } | null>(MAT_DIALOG_DATA);
  userForm = createUserDetailsForm();
  entitlements: UserEntitlements | null = null;
  fetchingUserData = false;
  saving = false;
  isCopyMode = false;
  readonly entitlementsConfig: { title: string; controlName: keyof UserEntitlements }[] = [
    { title: 'Permissions', controlName: 'permissions' },
    { title: 'Menu options', controlName: 'modules' },
    { title: 'Categories', controlName: 'shipmentDocumentCategories' }
  ];
  allAvailableColumns = [
    {
      key: 'firstName',
      name: 'First name'
    },
    {
      key: 'lastName',
      name: 'Last name'
    },
    {
      key: 'email',
      name: 'Email'
    }
  ];

  private dialogRef = inject(MatDialogRef<UserDetailsComponent>);
  private entitlementsService = inject(EntitlementsService);
  private destroyRef = inject(DestroyRef);
  private usersService = inject(UsersService);
  private matSnackBar = inject(MatSnackBar);
  private userService = inject(UserService);
  private errorHandler = inject(ErrorHandlerService);

  private readonly disabledModulesForExternals: { moduleName: ModuleName; canBeSetByAdmin: boolean }[] = [
    { moduleName: 'Admin', canBeSetByAdmin: false },
    { moduleName: 'Windward', canBeSetByAdmin: false }
    // { moduleName: 'VesselSchedules', canBeSetByAdmin: true }
  ];
  private readonly disabledFieldsInEditMode = ['email'];

  ngOnInit() {
    this.userForm
      .get('mainPartyNumber')
      ?.valueChanges.pipe(
        distinctUntilChanged(),
        tap(() => {
          this.clearPartyAccess('shipmentsAccess');
          this.clearPartyAccess('invoicesAccess');
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    this.userForm
      .get('email')
      ?.valueChanges.pipe(
        distinctUntilChanged(),
        tap(email => {
          // Auto-detect userType from email for new users and copy mode
          // This ensures business rules are enforced (enable/disable fields based on user type)
          if (!this.data?.id || this.data?.copyMode) {
            const detectedType = this.detectUserTypeFromEmail(email);
            const currentType = this.userTypeControl()?.value;
            if (detectedType !== currentType) {
              // Update userType to trigger business rule enforcement
              this.userTypeControl()?.setValue(detectedType, { emitEvent: true });
            }
          }
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();

    if (this.data?.id && !this.data?.copyMode) {
      this.getUserDetails();
      this.disableFieldsInEditMode();
    } else if (this.data?.id && this.data?.copyMode) {
      // Copy mode - userType will be disabled after data is loaded and patched
      this.getUserDetailsForCopy();
      // Enable email field for copy mode so admin can enter new email
      this.userForm.get('email')?.enable();
    } else {
      // New user mode - userType starts enabled for listener, then disabled
      this.userForm.get('userType')?.enable();
      // Subscribe to entitlements and user type changes
      this.handleEntitlements()
        .pipe(
          tap(({ shipmentDocumentCategories, modules }) => {
            this.selectAllCategoriesByDefault(shipmentDocumentCategories);
            this.selectDefaultModulesForNewUser(modules);
          }),
          switchMap(() => this.handleUserTypeChange()),
          takeUntilDestroyed(this.destroyRef)
        )
        .subscribe();
      // Disable userType after listener is set up - makes it readonly, auto-detected from email
      setTimeout(() => this.userForm.get('userType')?.disable(), 0);
    }
  }

  save() {
    if (this.userForm.invalid) {
      this.userForm.markAllAsTouched();
      return;
    }

    const isEditMode = !!this.data?.id && !this.isCopyMode;
    const userData = this.userForm.getRawValue();
    const request$ = isEditMode ? this.usersService.update(userData) : this.usersService.create(userData);
    this.saving = true;

    request$
      .pipe(
        tap(() => {
          this.matSnackBar.open(`User ${isEditMode ? 'updated' : 'created'} successfully`, 'x', {
            panelClass: 'snackbar-green'
          });
          this.dialogRef.close('Success');
          this.saving = false;
        }),
        catchError((error: HttpErrorResponse) => {
          let errorMessage = `Could not ${isEditMode ? 'update' : 'create'} the user. Please try again.`;
          // if (status === 409) {
          //   errorMessage = error.title;
          // }
          this.errorHandler.showApiError(
            error,
            errorMessage,
            this.allAvailableColumns,
            (fieldName, options) => options.find(x => x.key === fieldName)?.name
          );
          console.error(error);
          this.saving = false;
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  onCopyUser() {
    const userData = this.userForm.getRawValue();
    const copiedData = this.usersService.prepareCopyData(userData);
    const userName = `${userData.firstName} ${userData.lastName}`.trim();
    this.isCopyMode = true;
    this.userForm.reset();
    this.userForm.patchValue(copiedData);
    // Patch party arrays separately - patchValue doesn't work for FormArrays
    this.patchPartyValuesFor('shipmentsAccess', copiedData.shipmentsAccess);
    this.patchPartyValuesFor('invoicesAccess', copiedData.invoicesAccess);
    this.userForm.get('email')?.enable();
    // Disable userType so it's readonly - auto-detected from email only
    this.userForm.get('userType')?.disable();
    // Subscribe to user type changes to enforce business rules based on email detection
    this.handleUserTypeChange().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
    const message = userName
      ? `"${userName}" copied. Please fill in the required fields and save.`
      : 'User copied. Please fill in the required fields and save.';
    this.matSnackBar.open(message, 'x', {
      panelClass: 'snackbar-green'
    });
  }

  private getUserDetailsForCopy() {
    this.fetchingUserData = true;
    this.usersService
      .getUserById(this.data!.id ?? '')
      .pipe(
        tap(user => {
          const copiedData = this.usersService.prepareCopyData(user);
          const userName = `${user.firstName} ${user.lastName}`.trim();
          this.isCopyMode = true;
          this.userForm.patchValue(copiedData);
          // Patch party arrays separately - patchValue doesn't work for FormArrays
          this.patchPartyValuesFor('shipmentsAccess', copiedData.shipmentsAccess);
          this.patchPartyValuesFor('invoicesAccess', copiedData.invoicesAccess);
          this.userForm.get('email')?.enable();
          // Disable userType so it's readonly - auto-detected from email only
          this.userForm.get('userType')?.disable();
          this.fetchingUserData = false;
          const message = userName
            ? `"${userName}" copied. Please fill in the required fields and save.`
            : 'User copied. Please fill in the required fields and save.';
          this.matSnackBar.open(message, 'x', {
            panelClass: 'snackbar-green'
          });
        }),
        switchMap(() => this.handleEntitlements()),
        tap(() => {
          // Immediately disable admin permissions in copy mode after entitlements load
          this.disableAdminPermissionsInCopyMode();
        }),
        switchMap(() => this.handleUserTypeChange()),
        catchError(error => {
          this.matSnackBar.open('Could not fetch the user data. Please try again.', 'x');
          console.error(error);
          this.dialogRef.close();
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private getUserDetails() {
    this.fetchingUserData = true;
    this.usersService
      .getUserById(this.data!.id ?? '')
      .pipe(
        tap(({ shipmentsAccess, invoicesAccess, ...rest }) => {
          this.userForm.patchValue(rest);
          this.patchPartyValuesFor('shipmentsAccess', shipmentsAccess);
          this.patchPartyValuesFor('invoicesAccess', invoicesAccess);
          this.fetchingUserData = false;
        }),
        switchMap(() => this.handleEntitlements()),
        switchMap(() => this.handleUserTypeChange()),
        catchError(error => {
          this.matSnackBar.open('Could not fetch the user data. Please try again.', 'x');
          console.error(error);
          this.dialogRef.close();
          return NEVER;
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  private handleEntitlements() {
    return this.userService.isUserAdmin().pipe(
      switchMap(isAdmin =>
        this.entitlementsService.getEntitlements().pipe(
          map(entitlements => this.transformUserEntitlements(entitlements, isAdmin)),
          tap(entitlements => (this.entitlements = entitlements))
        )
      )
    );
  }

  private handleUserTypeChange() {
    return this.userTypeControl()!.valueChanges.pipe(
      startWith(this.userTypeControl()?.value),
      switchMap(userType =>
        this.userService.isUserAdmin().pipe(
          tap(isAdmin => {
            if (userType === 'Internal') {
              this.enableAllEntitlements(isAdmin);
              // In copy mode, disable admin permissions (Users.Admin, Tutorials.Edit)
              if (this.isCopyMode) {
                this.disableAdminPermissionsInCopyMode();
              }
            } else {
              const currentModules = this.userForm.get('modules')?.getRawValue() as string[];
              const filteredModules = currentModules.filter(module => {
                const disabledConfig = this.disabledModulesForExternals.find(({ moduleName }) => moduleName === module);
                return !disabledConfig || (isAdmin && disabledConfig.canBeSetByAdmin);
              });
              const currentPermissions = this.userForm.get('permissions')?.getRawValue() as string[];
              const filteredPermissions = currentPermissions.filter(
                permission => isAdmin && permission === 'Statuses.Advanced'
              );
              this.entitlements = this.transformUserEntitlements(this.entitlements!, isAdmin);
              this.userForm.get('permissions')?.setValue(filteredPermissions);
              this.userForm.get('modules')?.setValue(filteredModules);
              this.disableAccessToCustomersAndInvoices();
            }
          })
        )
      )
    );
  }

  private disableAccessToCustomersAndInvoices() {
    const accessToAllCustomersControl = this.userForm.get('canAccessAllCustomers');
    const accessToAllInvoicesControl = this.userForm.get('canAccessAllInvoices');
    accessToAllCustomersControl?.disable();
    accessToAllCustomersControl?.setValue(false);
    accessToAllInvoicesControl?.disable();
    accessToAllInvoicesControl?.setValue(false);
  }

  private transformUserEntitlements(entitlements: UserEntitlements, isAdmin: boolean) {
    const isExternal = this.isUserExternal() || !this.data?.id;

    return {
      ...entitlements,
      modules: entitlements.modules.map(module => ({
        ...module,
        disabled: this.isModuleDisabled(module.key, isAdmin, isExternal)
      })),
      permissions: entitlements.permissions.map(permission => ({
        ...permission,
        disabled: this.isPermissionDisabled(permission.key, isAdmin, isExternal)
      }))
    };
  }

  private isModuleDisabled(moduleKey: string, isAdmin: boolean, isExternal: boolean): boolean {
    if (isExternal) {
      const disabledConfig = this.disabledModulesForExternals.find(({ moduleName }) => moduleName === moduleKey);
      return !!(disabledConfig && !(isAdmin && disabledConfig.canBeSetByAdmin));
    }

    if (!isAdmin) {
      return moduleKey === 'Windward'; //moduleKey === 'VesselSchedules' ||
    }

    return false;
  }

  private isPermissionDisabled(permissionKey: string, isAdmin: boolean, isExternal: boolean): boolean {
    if (isExternal) {
      const isAdvancedStatus = permissionKey === 'Statuses.Advanced';
      return !(this.canEditPermissions(isAdmin) || (isAdmin && isAdvancedStatus));
    }

    return !isAdmin;
  }

  private canEditPermissions(isAdmin: boolean) {
    return isAdmin && !this.isUserExternal();
  }

  private clearPartyAccess(controlName: 'shipmentsAccess' | 'invoicesAccess') {
    (this.userForm.get(controlName) as FormArray).clear();
  }

  private patchPartyValuesFor(entity: 'shipmentsAccess' | 'invoicesAccess', parties: Party[]) {
    parties.forEach(({ number, name }) => {
      const partyGroup: PartyFormGroup = new FormGroup({
        number: new FormControl(number, { nonNullable: true }),
        name: new FormControl(name, { nonNullable: true })
      });
      (this.userForm.get(entity) as FormArray).push(partyGroup);
    });
  }

  private userTypeControl() {
    return this.userForm.get('userType');
  }

  private isUserExternal() {
    return this.userTypeControl()?.value === 'External';
  }

  private disableFieldsInEditMode() {
    this.disabledFieldsInEditMode.forEach(field => this.userForm.get(field)?.disable());
  }

  private selectAllCategoriesByDefault(categories: UserEntitlementOption[]) {
    return this.userForm.get('shipmentDocumentCategories')?.setValue(categories.map(({ key }) => key));
  }

  private selectDefaultModulesForNewUser(modules: UserEntitlementOption[]) {
    const defaultModuleKeys = ['Overview', 'Shipments', 'Containers', 'Documents', 'Contact', 'Archive', 'Tutorials'];
    const selectedModules = modules.filter(({ key }) => defaultModuleKeys.includes(key)).map(({ key }) => key);
    this.userForm.get('modules')?.setValue(selectedModules);
  }

  private enableAllEntitlements(isAdmin: boolean) {
    this.entitlements = this.transformUserEntitlements(this.entitlements!, isAdmin);
    // In copy mode, keep access checkboxes disabled for safety (default restriction)
    if (!this.isCopyMode) {
      this.userForm.get('canAccessAllCustomers')?.enable();
      this.userForm.get('canAccessAllInvoices')?.enable();
    }

    if (!isAdmin) {
      const currentModules = this.userForm.get('modules')?.getRawValue() as string[];
      const filteredModules = currentModules.filter(module => module !== 'Windward'); //module !== 'VesselSchedules' &&
      this.userForm.get('modules')?.setValue(filteredModules);
      this.userForm.get('permissions')?.setValue([]);
    }
  }

  private disableAdminPermissionsInCopyMode() {
    // In copy mode, mark admin permissions and restricted modules as disabled to prevent accidental privilege escalation
    if (this.entitlements?.permissions) {
      this.entitlements = {
        ...this.entitlements,
        permissions: this.entitlements.permissions.map(perm => ({
          ...perm,
          disabled: perm.key === 'Users.Admin' || perm.key === 'Tutorials.Edit' ? true : perm.disabled
        }))
      };
    }

    // Also disable restricted modules in copy mode
    if (this.entitlements?.modules) {
      this.entitlements = {
        ...this.entitlements,
        modules: this.entitlements.modules.map(module => ({
          ...module,
          disabled: module.key === 'Admin' || module.key === 'Windward' ? true : module.disabled
        }))
      };
    }
  }

  private detectUserTypeFromEmail(email: string): 'Internal' | 'External' {
    if (!email || email.indexOf('@') === -1) return 'External';
    const domain = email.substring(email.indexOf('@') + 1);
    return domain.toLowerCase() === 'scangl.com' ? 'Internal' : 'External';
  }
}
